import React, { useCallback, useEffect, useRef } from 'react';
import useDebounce from '../../utils/useDebounce';
import { Modal, Form, Button, Col, Row, Spinner } from 'react-bootstrap';
import { connect } from 'react-redux';
import useComponentState from '../../utils/useComponentState';
import CategorySelect from '../../screens/AddProducts/utils/CategorySelect';
import {
  PRODUCT_FORM_FIELDS as FORM_FIELDS,
  buildPayload,
  DEBOUNCE_DELAY,
} from '../../screens/AddProducts/utils/AddProductsConstants';
import VoucherThemeSelect from '../../screens/AddProducts/utils/VoucherThemeSelect';
import AddOnsSelect from '../../screens/AddProducts/utils/AddOnsSelect';
import { searchProductCategoryDropdownList, updateProduct } from '../../api/Products';
import { searchVoucherThemeDropdownList } from '../../api/voucherTheme';
import { searchAddOnDropdownList } from '../../api/Vouchers';
import { searchMerchantDropdownList } from '../../api/merchant';
import styles from './EditProductModal.module.scss';
import { toast } from 'react-toastify';
import MerchantGroupSelect from '../../screens/AddProducts/utils/MerchantGroupSelect';
import ClientSelect from '../../screens/AddProducts/utils/ClientSelect';
import { searchClientDropdownList } from '../../api/Client';

const EditProductModal = ({ show, onHide, product, auth, onSuccess }) => {
  const [state, setState] = useComponentState({
    formData: {
      productTitle: '',
      description: '',
      selectedCategoryId: '',
      selectedVoucherThemes: null,
      selectedAddOns: [],
      selectedMerchantGroupId: '',
      currentCategory: '',
      clientId: '',
    },
    categories: [],
    allThemes: [],
    alreadyHaveThemes: [],
    addOns: [],
    merchantGroups: [],
    searchType: 'id',
    searchTerm: '',
    merchantGroupsLoading: false,
    validated: false,
    loading: false,
    initLoading: true,
    clientId: '',
    clients: [],
    clientSearchType: 'name',
    clientSearchTerm: '',
    loadingClients: false,
  });

  // Fetch categories, themes, and add-ons once token is ready
  const authToken = auth?.auth?.token;

  // Track latest request to ignore stale responses and support aborting
  const latestReqIdRef = useRef(0);
  const abortCtrlRef = useRef(null);

  // Debounce the search term so we only issue requests after user stops typing
  const debouncedSearchTerm = useDebounce(state.searchTerm, DEBOUNCE_DELAY);
  const debouncedClientSearchTerm = useDebounce(state.clientSearchTerm, DEBOUNCE_DELAY);

  // Perform merchant group search whenever the debounced term or filter type changes
  const fetchMerchantGroupsWithSearch = async (term, searchType, token) => {
    // Cancel previous in-flight call
    if (abortCtrlRef.current) abortCtrlRef.current.abort();

    const reqId = ++latestReqIdRef.current;
    const controller = new AbortController();
    abortCtrlRef.current = controller;

    setState((prev) => ({ ...prev, merchantGroupsLoading: true }));

    try {
      let resp;
        resp = await searchMerchantDropdownList(
          token,
          term,
          searchType || 'id',
          'name',
          'asc',
          { signal: controller.signal }
        );

      setState((prev) => ({
        ...prev,
        merchantGroups: resp.merchantGroups || [],
        merchantGroupsLoading: false,
      }));
    } catch (err) {
      if (reqId !== latestReqIdRef.current) return;
      if (err?.name === 'AbortError') return;

      setState((prev) => ({ ...prev, merchantGroupsLoading: false }));
    }
  };

  const fetchClientsWithSearch = useCallback(async (term, searchType, token) => {
    // abort previous
    if (abortCtrlRef.current) abortCtrlRef.current.abort();

    const reqId = ++latestReqIdRef.current;
    const controller = new AbortController();
    abortCtrlRef.current = controller;

    setState(prev => ({ ...prev, loadingClients: true }));

    try {
      let response;
      // With search term → perform filtered search
      response = await searchClientDropdownList(token, term, searchType || "name", "clientName", "asc");
      response = response?.clients || response || [];

      // ignore stale responses
      if (reqId !== latestReqIdRef.current) return;

      setState(prev => ({ ...prev, clients: response, loadingClients: false }));
    } catch (err) {
      if (reqId !== latestReqIdRef.current) return;
      if (err?.name === 'AbortError') return; // request cancelled

      toast.error('Failed to search clients');
      setState(prev => ({ ...prev, loadingClients: false }));
    }
  }, []);

  useEffect(() => {
    const term = debouncedSearchTerm?.toString().trim();
    fetchMerchantGroupsWithSearch(term, state.searchType, authToken);

    return () => {
      if (abortCtrlRef.current) abortCtrlRef.current.abort();
    };
  }, [debouncedSearchTerm, state.searchType, authToken]);

  // Load product data into form when modal opens
  useEffect(() => {
    if (!product) return;

    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        productTitle: product.title || '',
        description: product.description || '',
        currentCategory: product.productCategoryId || '',
        selectedCategoryId: product.productCategoryId || '',
        selectedAddOns: product.addOns?.map(addOn => addOn.id) || [],
        /* For edit mode we keep a single theme object (default true). If product has multiple themes,
         pick the first non-default or first in list to display as the current selection.
         */
        clientId: product.clientId || null,
        selectedVoucherThemes:
          product.themes && product.themes.length > 0
            ? product.themes.filter((theme) => theme?.default === true)[0]
            : null,
        selectedMerchantGroupId: product.merchantGroup.id || '',
        slug: product.slug || '',
      },
      alreadyHaveThemes:
        product.themes.filter((theme) => theme?.default === true) || [],
    }));
  }, [product]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (!authToken) {
          setState((prev) => ({ ...prev, initLoading: false }));
          return;
        }

        const [categories, themes, addOns] =
          await Promise.all([
            searchProductCategoryDropdownList(authToken, "", "", "category", "asc"),
            searchVoucherThemeDropdownList(authToken, "", "", "name", "asc"),
            searchAddOnDropdownList(authToken, "", "", "name", "asc"),
          ]);

        // Handle potential different response structures
        const categoriesList = categories?.productCategories || categories || [];
        const themesList = themes?.voucherThemes || themes || [];

        setState((prev) => ({
          ...prev,
          categories: categoriesList || [],
          allThemes: themesList || [],
          addOns: addOns?.addOns || [],
          initLoading: false,
        }));
      } catch (err) {
        setState((prev) => ({
          ...prev,
          initLoading: false,
        }));
      }
    };

    // Only fetch when valid token exists
    if (authToken) {
      fetchInitialData();
    } else {
      setState((prev) => ({
        ...prev,
        initLoading: false,
      }));
    }
  }, [authToken]);

  // Input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, [name]: value },
    }));
  };

  const handleCategoryChange = (payload) => {
    const categoryId = payload?.target?.value;
    // Find the selected category to get its name
    const selectedCategory = state.categories.find(
      (cat) => String(cat.id) === String(categoryId)
    );

    setState((prev) => ({
      ...prev,
      formData: {
        ...prev.formData,
        selectedCategoryId: categoryId, // Store ID for backend
        currentCategory: selectedCategory ? selectedCategory.id : '', // Store name for display
      },
    }));
  };

  // handlers for merchant group search inputs
  const setSearchType = (val) =>
    setState((prev) => ({ ...prev, searchType: val }));

  const setSearchTerm = (val) =>
    setState((prev) => ({ ...prev, searchTerm: val }));

  const handleThemesChange = (themes) => {
    // In edit mode VoucherThemeSelect will call onChange with an event-like object
    // where target.value is a single theme object (or null).
    const newTheme = themes && themes.target ? themes.target.value : themes;
    setState((prev) => ({
      ...prev,
      // keep a copy for any display/use elsewhere
      alreadyHaveThemes: newTheme ? [newTheme] : [],
      formData: { ...prev.formData, selectedVoucherThemes: newTheme },
    }));
  };

  const handleAddOnsChange = (payload) => {
    const raw = payload?.target ? payload.target.value : payload;
    const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, selectedAddOns: arr },
    }));
  };

  const handleMerchantGroupChange = (payload) => {
    const value = payload?.target?.value;
    setState((prev) => ({
      ...prev,
      formData: { ...prev.formData, selectedMerchantGroupId: value },
    }));
  };

  // Handle save
  const handleSave = async (e) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, loading: true }));

    try {
      const id = product?.id;
      if (!id) throw new Error('Missing product id');

      // Build payload using the same builder used for AddProducts so types/keys match
      const payload = buildPayload({
        productTitle: state.formData.productTitle,
        slug: state.formData.slug,
        description: state.formData.description,
        selectedCategoryId: state.formData.selectedCategoryId,
        selectedVoucherThemes: state.formData.selectedVoucherThemes,
        selectedAddOns: state.formData.selectedAddOns,
        selectedMerchantGroupId: state.formData.selectedMerchantGroupId,
        clientId: state.formData.clientId,
      });

      // (buildPayload adds these with defaults; omit them for update)
      if (payload.hasOwnProperty('featured')) delete payload.featured;
      if (payload.hasOwnProperty('published')) delete payload.published;
      if (payload.hasOwnProperty('deliveryOptions'))
        delete payload.deliveryOptions;

      const response = await updateProduct(authToken, id, payload);

      if (response) {
        toast.success(`Product ${response.id} Updated Successfully!`);
        onSuccess();
      }
      onHide();
    } catch (err) {
      toast.error(
        'Failed to update product: ' + (err.message || 'Unknown error')
      );
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    const term = debouncedClientSearchTerm?.toString().trim();
    fetchClientsWithSearch(term, state.clientSearchType, auth.auth.token);

    // cleanup when dependency changes
    return () => {
      if (abortCtrlRef.current) abortCtrlRef.current.abort();
    };
  }, [debouncedClientSearchTerm, state.clientSearchType, auth.auth.token, fetchClientsWithSearch]);

  // Render modal immediately. Show a non-blocking loading overlay inside the modal
  // while initial data (categories/themes/addOns/merchantGroups) is being fetched.
  // This ensures the modal opens immediately and the UI indicates loading state.

  //  MAIN UI
  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.editProductModal}
      centered
      size='lg'
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Product</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Loading overlay: absolutely positioned over modal body when initLoading */}
        {state.initLoading && (
          <div className={styles.loadingOverlay}>
            <Spinner animation='border' />
            <p className='mt-3 mb-0'>Loading product data...</p>
          </div>
        )}
        <Form noValidate validated={state.validated} onSubmit={handleSave}>
          {/* Category Select */}
          <Form.Group className='mb-3'>
            <CategorySelect
              categories={state.categories || []}
              edit
              value={state.formData.currentCategory}
              onChange={handleCategoryChange}
              validated={state.validated}
              disabled={state.initLoading} // Prevents interaction while loading (kept for consistency)
            />
          </Form.Group>

          {/* Client Select */}
          <Form.Group className='mb-3'>
            <ClientSelect
              clients={state.clients}
              value={state.formData.clientId}
              onChange={handleInputChange}
              error={state.fieldErrors?.clientId}
              loading={state.loadingClients}
              searchType={state.clientSearchType}
              setSearchType={(name) => setState({ clientSearchType: name })}
              searchTerm={state.clientSearchTerm}
              setSearchTerm={(name) => {
                setState({ clientSearchTerm: name });
                // the effect watching debouncedClientSearchTerm will handle fetching
              }}
            />
          </Form.Group>

          {/* Voucher Themes */}
          <Form.Group className='mb-3'>
            <VoucherThemeSelect
              voucherThemes={state.allThemes || []}
              edit
              // pass single theme object for edit mode
              value={state.formData.selectedVoucherThemes}
              onChange={handleThemesChange}
              validated={state.validated}
              disabled={state.initLoading}
            />
          </Form.Group>

          {/* AddOns */}
          <Form.Group className='mb-3'>
            <AddOnsSelect
              addOns={state.addOns || []}
              value={state.formData.selectedAddOns || []}
              onChange={handleAddOnsChange}
              validated={state.validated}
              disabled={state.initLoading}
            />
          </Form.Group>

          {/* Merchant Group Select */}
          <Form.Group className='mb-3'>
            <MerchantGroupSelect
              value={state.formData.selectedMerchantGroupId}
              onChange={handleMerchantGroupChange}
              merchantGroups={state.merchantGroups}
              loading={state.merchantGroupsLoading}
              searchType={state.searchType}
              setSearchType={setSearchType}
              searchTerm={state.searchTerm}
              setSearchTerm={setSearchTerm}
              validated={state.validated}
              edit
              disabled={state.merchantGroupsLoading}
            />
          </Form.Group>

          {/* Text Fields */}
          {FORM_FIELDS.map(
            ({ name, label, type, placeholder, required, rows }) => (
              <Form.Group as={Row} className='mb-3' key={name}>
                <Form.Label column sm={3}>
                  {label} {required && <span className='text-danger'>*</span>}
                </Form.Label>
                <Col sm={9}>
                  <Form.Control
                    as={type === 'textarea' ? type : undefined}
                    type={type !== 'textarea' ? type : undefined}
                    name={name}
                    value={state.formData[name] ?? ''}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    rows={rows}
                    required={required}
                    disabled={state.initLoading}
                  />
                </Col>
              </Form.Group>
            )
          )}

          {/* Buttons */}
          <div className='d-flex justify-content-end'>
            <Button variant='secondary' onClick={onHide} className='me-2'>
              Cancel
            </Button>
            <Button type='submit' disabled={state.loading || state.initLoading}>
              {state.loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(EditProductModal);
